import { ComposedSurveyPlugin, IPluginExecutionContext } from "@hyper-inferno/survey-elements-core";



/**

* Diary Study Task Tracker Configuration

* Enforces daily task limits and renders a dynamic progress dashboard.

*/

export const diaryTaskTracker = {

    id: "f29570a8-3bdf-4b79-b99c-0680c3a0697e",

    usageId: "fe32af70-8715-45b3-a5fb-ee4467ebc054",

    name: "Diary Study Task Tracker",

    html: "<div id=\"diary-dashboard-wrapper\" style=\"font-family: system-ui, sans-serif; padding: 15px; background: #fdfdfd; border: 1px solid #e2e8f0; border-radius: 8px; max-width: 800px;\">\n <div style=\"display: flex; justify-content: space-between; align-items: center;\">\n <span style=\"font-weight: 600; color: #334155; font-size: 15px;\">📅 Diary Study Dashboard</span>\n <button id=\"diary-toggle-btn\" style=\"background: #4f46e5; color: white; border: none; padding: 6px 14px; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500;\">\n Show Pending Tasks (<span id=\"diary-pending-count\">0</span>)\n </button>\n </div>\n \n <div id=\"diary-task-list\" style=\"display: none; margin-top: 15px; padding-top: 12px; border-top: 1px solid #edf2f7;\">\n <ul id=\"diary-tasks-ul\" style=\"list-style: none; padding: 0; margin: 0;\"></ul>\n </div>\n</div>\n\n<div id=\"diary-lockout-modal\" style=\"display: none; position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(4px); z-index: 99999; justify-content: center; align-items: center;\">\n <div style=\"background: white; padding: 35px; border-radius: 12px; max-width: 440px; width: 90%; text-align: center; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);\">\n <div style=\"font-size: 50px; margin-bottom: 12px;\">⏳</div>\n <h3 style=\"margin: 0 0 10px 0; color: #1e293b; font-size: 22px; font-weight: 700;\">Daily Limit Reached</h3>\n <p style=\"color: #64748b; font-size: 15px; line-height: 1.6; margin-bottom: 0;\">Please try again tomorrow! You have completed the maximum number of diary tasks allowed for today. The layout is locked.</p>\n </div>\n</div>",



    context: {

        type: "global",

        surveyId: 1,

        pageId: null,

        elementId: null

    },



    instanceValues: {

        task_mapping: [

            { label: "Intro", page_id: "3" },

            { label: "Test Task", page_id: "4" }

        ],

        max_tasks_per_day: 3

    },



    variable_schema: {

        type: "object",

        properties: {

            diary_history: {

                type: "object",

                description: "Simple key-value ledger tracking completion: { page_id: 'YYYY-MM-DD' }"

            },

            diary_lockout_active: {

                type: "boolean",

                description: "True if the user has reached their maximum limit for today."

            }

        }

    },



    hooks: {

        /**
        
        * 1. PAGE INITIALIZATION
        
        * Sets up variables, renders the task UI list, and checks for lockout state.
        
        */

        onPageInit(context: IPluginExecutionContext): void {

            // Setup configuration constants

            const maxTasks = context.config['max_tasks_per_day'] || 3;

            const taskMap = context.config['task_mapping'] || [];

            const todayStr = new Date().toISOString().split('T')[0];



            // Read & parse completion history from engine storage

            let history = context.pluginData()?.history ?? {};

            if (typeof history === 'string') {

                try {

                    history = JSON.parse(history);

                } catch (e) {

                    history = {};

                }

            }



            // Save metadata to context execution track for later hooks

            context.state.todayStr = todayStr;

            context.state.maxTasks = maxTasks;

            context.state.taskMap = taskMap;



            // Calculate task statistics

            const completedTodayCount = Object.values(history).filter(date => date === todayStr).length;

            const pendingTasksCount = taskMap.filter((task: any) => !history[task.page_id]).length;



            // Grab UI Elements

            const pendingCountSpan = context.container.querySelector('#diary-pending-count');

            const toggleBtn = context.container.querySelector('#diary-toggle-btn');

            const taskListDiv = context.container.querySelector('#diary-task-list');

            const tasksUl = context.container.querySelector('#diary-tasks-ul');



            // Update pending count display

            if (pendingCountSpan) {

                pendingCountSpan.innerHTML = pendingTasksCount;

            }



            // Render the Task List UI

            if (tasksUl) {

                tasksUl.innerHTML = '';

                taskMap.forEach((task: any) => {

                    const completionDate = history[task.page_id];

                    const isDone = !!completionDate;



                    const li = document.createElement('li');

                    li.style.cssText = 'padding: 10px 0; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f1f5f9; font-size: 14px;';



                    const statusLabel = isDone ? `✓ Completed (${completionDate})` : '⏳ Pending';

                    const statusColor = isDone ? '#10b981' : '#f59e0b';

                    const textColor = isDone ? '#94a3b8' : '#334155';

                    const textDecoration = isDone ? 'line-through' : 'none';



                    li.innerHTML = `

<span style="color: ${textColor}; text-decoration: ${textDecoration}">${task.label}</span>

<span style="font-weight: 600; color: ${statusColor}; font-size: 13px;">${statusLabel}</span>

`;

                    tasksUl.appendChild(li);

                });

            }



            // Setup show/hide panel toggle interaction

            if (toggleBtn && taskListDiv) {

                context.state.toggleHandler = () => {

                    const isHidden = taskListDiv.style.display === 'none';

                    taskListDiv.style.display = isHidden ? 'block' : 'none';

                    toggleBtn.innerText = isHidden ? 'Hide Task List' : `Show Pending Tasks (${pendingTasksCount})`;

                };

                toggleBtn.addEventListener('click', context.state.toggleHandler);

            }



            // Evaluate and enforce Lockout Rules

            if (completedTodayCount >= maxTasks) {

                context.patchData({ 'diary_lockout_active': true });

                const modal = context.container.querySelector('#diary-lockout-modal');



                if (modal) {

                    modal.style.display = 'flex';



                    // Freeze every interface element across the survey container view window

                    const viewElement = context.container.parentElement || context.container;

                    const interactiveElements = viewElement.querySelectorAll('input, select, textarea, button, a');



                    interactiveElements.forEach((el: any) => {

                        if (!modal.contains(el) && el.id !== 'diary-toggle-btn') {

                            el.setAttribute('disabled', 'true');

                            el.style.pointerEvents = 'none';

                            el.style.opacity = '0.5';

                        }

                    });

                }

            } else {

                context.patchData({ 'diary_lockout_active': false });

            }

        },



        /**
        
        * 2. DATA CHANGE MONITORING
        
        * Lightweight verification pass loop.
        
        */

        onDataChange(context: IPluginExecutionContext): void {

        },



        /**
        
        * 3. BEFORE SUBMIT VALIDATION
        
        * Prevents sneaky multi-window submissions if daily limits are already breached.
        
        */

        onBeforeSubmit(context: IPluginExecutionContext): void | boolean {

            const history = context.pluginData().history || {};

            const todayStr = context.state.todayStr;

            const currentPageId = context.pageId



            console.log(currentPageId, context)

            console.log(!currentPageId || history[currentPageId])

            console.log(history, currentPageId)



            // Exit immediately if page is unrecognized or already historically completed

            if (!currentPageId || history[currentPageId]) {

                return;

            }



            // Re-verify counts prior to committing updates safely

            const completedTodayCount = Object.values(history).filter(date => date === todayStr).length;





            console.log(completedTodayCount)





            if (completedTodayCount >= context.state.maxTasks) {

                context.setInvalid('Daily completion limit already reached. Please complete the task tommorow.');

                return false;

            }



            // Save current page completion to the tracking history ledger

            history[currentPageId] = todayStr;

            context.patchData({ 'history': history });

            console.log("End of before submit")



        },



        /**
        
        * 4. CLEANUP
        
        * Tears down active event listeners to prevent browser memory leaks.
        
        */

        onCleanup(context: IPluginExecutionContext): void {

            if (context.state.toggleHandler) {

                const toggleBtn = context.container.querySelector('#diary-toggle-btn');

                if (toggleBtn) {

                    toggleBtn.removeEventListener('click', context.state.toggleHandler);

                }

            }

        }

    }

};

